import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import { Icon } from "@iconify/react";
import { LoadingButton } from "@mui/lab";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "src/Component/Snakbar";
import CustomCircularProgress from "src/Component/custom/CustomCircularProgress";
import CustomPreviewCard from "src/Component/custom/CustomPreviewCard";
import { uploadPsychometricDocumentAsync } from "src/redux/services/psychometricService";
import { getUploadedPsychometricDocumentsAsync } from "src/redux/services/psycometricServices";
import {
  CreateTableAsync,
  getCounsellorLogListAsync,
} from "src/redux/services/table";
import ProfileBuildingForm from "./ProfileBuildingForm";
import BasicTable from "./Table";

export default function Right({ title }) {
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { getUploadedPsychometricDocuments, isLoading } = useSelector(
    (state) => state.psychometric,
  );

  const token = sessionStorage.getItem("authToken");
  const [getCounsellingData, setGetCounsellingData] = useState([]);
  const [hasJoinedMeeting, setHasJoinedMeeting] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isNewFileUploaded, setIsNewFileUploaded] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [trackDuration, setTrackDuration] = useState("");
  const [errors, setErrors] = useState({});
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const status = getUploadedPsychometricDocuments?.reportStatus;
  const hasFiles = uploadedFiles.length > 0;

  dayjs.extend(utc);
  dayjs.extend(timezone);

  const localTime = getCounsellingData?.assignedDate
    ? dayjs.utc(getCounsellingData.assignedDate).local()
    : null;

  const dateOnly = localTime ? localTime.format("DD/MM/YYYY") : "-";
  const timeOnly = localTime ? localTime.format("hh:mm A") : "-";

  const handleFileChange = useCallback((event) => {
    const files = Array.from(event.target.files);

    if (files.length > 0) {
      const fileReaders = files.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = () => {
            const base64 = reader.result;
            resolve({
              url: base64,
              name: file.name,
              type: file.type,
              file,
            });
          };

          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
      });

      Promise.all(fileReaders)
        .then((newFiles) => {
          setUploadedFiles(newFiles);
          setIsNewFileUploaded(true);
          event.target.value = "";
        })
        .catch((error) => {
          console.error("Error reading files:", error);
        });
    }
  }, []);

  const handleSubmitFile = async () => {
    if (!isNewFileUploaded || uploadedFiles.length === 0) {
      return;
    }

    const file = uploadedFiles[0];

    if (!file.file && typeof file.url === "string") {
      return;
    }

    const payload = {
      testReport: file.url,
      reportStatus: "Pending",
    };

    try {
      setIsSubmitting(true);

      const res = await dispatch(
        uploadPsychometricDocumentAsync(payload),
      ).unwrap();

      console.log("res", res);

      showSnackbar(res?.payload?.message || res?.message, "success");

      setIsNewFileUploaded(false);

      dispatch(getUploadedPsychometricDocumentsAsync());
    } catch (error) {
      console.log("error", error);

      showSnackbar(
        error?.message || "Submission failed. Please try again.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}personal-counsellor/student`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      const personalCounsellorData = response?.data?.data;
      setGetCounsellingData(personalCounsellorData);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleDateTimeChange = (newValue) => {
    const date = dayjs(newValue);
    setSelectedDateTime(date);
  };

  const validateFields = () => {
    const newErrors = {};

    if (!selectedDateTime) {
      newErrors.selectedDateTime = "Please select a valid date and time.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRescheduleSubmit = async () => {
    if (!validateFields()) {
      return;
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}personal-counsellor/update-meeting`,
        {
          assignedDate: selectedDateTime,
          duration: trackDuration,
          isReschedule: true,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      showSnackbar(response.data.message, "success");
      setIsDialogOpen(false);
      fetchUserData();
      setTrackDuration("");
      setSelectedDateTime(null);
      setHasJoinedMeeting(false);
    } catch (error) {
      console.error("Error rescheduling the meeting:", error);
    }
  };

  useEffect(() => {
    fetchUserData();
    dispatch(getUploadedPsychometricDocumentsAsync());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleJoinMeeting = () => {
    if (!getCounsellingData?.meetingLink) {
      showSnackbar("No meeting link available", "error");
      return;
    }

    // Open meeting in new tab
    window.open(getCounsellingData.meetingLink, "_blank");

    // Set joined status to true
    setHasJoinedMeeting(true);

    const payload = {
      shouldCreateLog: true,
      counsellorId: getCounsellingData?.counsellorId?._id,
      assignedDate: getCounsellingData?.assignedDate,
    };

    dispatch(CreateTableAsync(payload))
      .then((res) => {
        if (res.payload.status === 200) {
          showSnackbar("Meeting joined successfully!", "success");
          dispatch(getCounsellorLogListAsync());
        } else if (res.payload.status === 400) {
          showSnackbar(res.payload.message, "error");
        }
      })
      .catch((error) => {
        console.error("Error joining meeting:", error);
        showSnackbar("Error joining meeting", "error");
      });
  };

  useEffect(() => {
    if (getUploadedPsychometricDocuments?.testReport && !isNewFileUploaded) {
      const url = getUploadedPsychometricDocuments.testReport;
      const ext = url.split(".").pop().toLowerCase();

      const mimeTypeMap = {
        pdf: "application/pdf",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      };

      const fileType = mimeTypeMap[ext] || "application/octet-stream";

      const previewDoc = {
        url,
        name: `Uploaded_Report.${ext}`,
        type: fileType,
        file: null,
      };

      setUploadedFiles([previewDoc]);
    }
  }, [getUploadedPsychometricDocuments, isNewFileUploaded]);

  useEffect(() => {
    dispatch(getUploadedPsychometricDocumentsAsync());
  }, [dispatch]);

  const getStatusBgColor = (reportStatusValue) => {
    if (reportStatusValue === "Approved") return "success.main";
    if (reportStatusValue === "Pending") return "warning.main";
    if (reportStatusValue === "Not Approved") return "error.main";
    return "error.main";
  };

  // CORRECTED time calculation for join button
  useEffect(() => {
    const calculateTimeUntilMeeting = () => {
      const now = dayjs();

      // Check if we have valid meeting data
      if (!getCounsellingData?.assignedDate) {
        setIsButtonEnabled(false);
        return;
      }

      const assignedTime = dayjs(getCounsellingData.assignedDate);

      if (!assignedTime.isValid()) {
        setIsButtonEnabled(false);
        return;
      }

      const tenMinutesBefore = assignedTime.subtract(10, "minute");
      const meetingDuration = getCounsellingData?.duration || 60;
      const meetingEnd = assignedTime.add(meetingDuration, "minute");

      // If meeting has already ended - DISABLE BUTTON
      if (now.isAfter(meetingEnd)) {
        setIsButtonEnabled(false);

        return;
      }

      // If within the meeting time window (10 mins before to meeting end) - ENABLE BUTTON
      if (now.isAfter(tenMinutesBefore) && now.isBefore(meetingEnd)) {
        setIsButtonEnabled(true);

        return;
      }

      // If meeting is in the future (before 10-minute window) - DISABLE BUTTON
      if (now.isBefore(tenMinutesBefore)) {
        setIsButtonEnabled(false);

        return;
      }

      // Default case - DISABLE BUTTON
      setIsButtonEnabled(false);
    };

    calculateTimeUntilMeeting();

    // Update every 30 seconds for real-time countdown
    const interval = setInterval(calculateTimeUntilMeeting, 30000);

    return () => clearInterval(interval);
  }, [getCounsellingData?.assignedDate, getCounsellingData?.duration]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper
        elevation={4}
        sx={{
          display: { xs: "none", sm: "flex" },
          flexDirection: "column",
          padding: { xs: "10px", sm: "3rem" },
          margin: "auto",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-around",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Box
            sx={{
              padding: { xs: "0px", sm: "32px" },
              borderRadius: "10px",
              backgroundColor: "#E2FEFF",
              border: "1px solid #ccc",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              flex: 2,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 2, p: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: "20px", sm: "24px", md: "32px" },
                  fontWeight: "600",
                  color: "#00999E",
                }}
              >
                {title}
              </Typography>
            </Box>
            {title === "Profile Building" && (
              <Box
                sx={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "20px",
                  border: "1px solid #ccc",
                  backgroundColor: "#FFF",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 2,
                  mx: "auto",
                }}
              >
                <ProfileBuildingForm />
              </Box>
            )}

            <>
              {title === "Psychometric Test" ? (
                isLoading ? (
                  <CustomCircularProgress />
                ) : (
                  <Box
                    sx={{
                      width: "97%",
                      padding: { xs: "16px", sm: "24px" },
                      borderRadius: "20px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #ccc",
                      boxSizing: "border-box", // Prevent overflow
                      margin: {
                        xs: "7px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: "column",
                        mb: 2,
                      }}
                    >
                      {getUploadedPsychometricDocuments !== null && (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Chip
                            label={
                              getUploadedPsychometricDocuments?.reportStatus
                            }
                            sx={{
                              bgcolor: getStatusBgColor(
                                getUploadedPsychometricDocuments?.reportStatus,
                              ),
                              color: "#fff",
                              fontWeight: "semibold",
                              textTransform: "capitalize",
                              textAlign: "center",
                              padding: 2,
                            }}
                            size="small"
                          />
                        </Box>
                      )}

                      <Typography
                        variant="h6"
                        sx={{ fontSize: 18, fontWeight: "600" }}
                      >
                        Upload Your Psychometric Test Report
                      </Typography>

                      {status === "Not Approved" && (
                        <Typography
                          sx={{
                            mt: 2,
                            color: "error.main",
                            fontWeight: "500",
                            textAlign: "center",
                          }}
                        >
                          {getUploadedPsychometricDocuments?.remark}
                        </Typography>
                      )}

                      {(status === undefined || status === "Not Approved") && (
                        <Box
                          sx={{
                            border: "1px dashed grey",
                            borderRadius: "5px",
                            bgcolor: "#DEF2F3",
                            textAlign: "center",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            p: 2,
                            mt: 2,
                            width: "100%",
                          }}
                        >
                          <Icon
                            icon="flowbite:upload-solid"
                            style={{ color: "#80869A", fontSize: "50px" }}
                          />
                          <Typography variant="subtitle2" sx={{ fontSize: 18 }}>
                            Drag and Drop or Select to Upload Documents
                          </Typography>
                          <Typography variant="caption">
                            Supports PDF, Images or Word File
                          </Typography>
                          <Button
                            variant="contained"
                            component="label"
                            sx={{
                              bgcolor: "#fff",
                              width: "150px",
                              border: "1px solid #00999E",
                              color: "#00999E",
                              mt: 2,
                            }}
                          >
                            Select File
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              onChange={handleFileChange}
                            />
                          </Button>
                        </Box>
                      )}

                      {hasFiles && (
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            gap: 3,
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Uploaded Documents:
                          </Typography>
                          {uploadedFiles.map((doc, index) => (
                            <CustomPreviewCard
                              key={`doc-${index}`}
                              doc={doc}
                              index={index}
                            />
                          ))}
                        </Box>
                      )}
                      {status !== "Pending" && status !== "Approved" && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {(() => {
                            const isSubmitDisabled =
                              (!isNewFileUploaded &&
                                !!getUploadedPsychometricDocuments?.testReport) ||
                              uploadedFiles.length === 0;

                            return (
                              <>
                                <LoadingButton
                                  variant="contained"
                                  sx={{
                                    mt: 2,
                                    backgroundColor: "#00999E",
                                    "&:hover": {
                                      backgroundColor: "#007f80",
                                      color: "#fff",
                                    },
                                    width: { xs: "110px", sm: "160px" },
                                    fontSize: 13,
                                    padding: "7px 4px",
                                  }}
                                  onClick={handleSubmitFile}
                                  disabled={isSubmitDisabled}
                                  loading={isSubmitting}
                                >
                                  Submit Report
                                </LoadingButton>

                                {!isSubmitDisabled && (
                                  <LoadingButton
                                    variant="contained"
                                    onClick={() => setUploadedFiles([])}
                                    sx={{
                                      mt: 2,
                                      backgroundColor: "#dc3545",
                                      "&:hover": {
                                        backgroundColor: "#c82333",
                                        color: "#fff",
                                      },
                                      width: { xs: "110px", sm: "160px" },
                                      fontSize: 13,
                                      padding: "7px 4px",
                                    }}
                                  >
                                    Remove
                                  </LoadingButton>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )
              ) : (
                title === "Counselling" && (
                  <Box
                    sx={{
                      width: "97%",
                      padding: { xs: "16px", sm: "24px" },
                      borderRadius: "20px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #ccc",
                      boxSizing: "border-box", // Prevent overflow
                      margin: {
                        xs: "7px",
                      },
                    }}
                  >
                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Grid container>
                          <Grid
                            item
                            xs={4}
                            sx={{
                              display: "flex",
                              justifyContent: {
                                xs: "flex-start",
                                sm: "flex-start",
                              },
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                            >
                              Date:
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            xs={8}
                            sx={{
                              display: "flex",
                              justifyContent: {
                                xs: "flex-end",
                                sm: "flex-end",
                                md: "center",
                              },
                            }}
                          >
                            <Typography
                              sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                            >
                              {dateOnly}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Grid container>
                          <Grid
                            item
                            xs={6}
                            sx={{
                              display: "flex",
                              justifyContent: {
                                xs: "flex-start",
                                sm: "center",
                              },
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                            >
                              Time:
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            xs={6}
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Typography
                              sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                            >
                              {timeOnly}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Time status indicator
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mb: 2,
                        p: 1,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: isButtonEnabled ? "success.main" : "text.secondary",
                          fontWeight: isButtonEnabled ? "bold" : "normal",
                        }}
                      >
                        {timeUntilMeeting}
                      </Typography>
                    </Box> */}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                      >
                        Counsellor Name
                      </Typography>
                      <Typography sx={{ fontSize: { xs: "14px", sm: "16px" } }}>
                        {getCounsellingData?.counsellorId?.name ??
                          "- Not Assigned -"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                      >
                        Meeting Link
                      </Typography>
                      <Button
                        variant="contained"
                        disabled={
                          !isButtonEnabled ||
                          !getCounsellingData?.meetingLink ||
                          hasJoinedMeeting
                        }
                        sx={{
                          borderRadius: 2,
                          backgroundColor: hasJoinedMeeting
                            ? "success.main"
                            : "#00999E",
                          "&:hover": {
                            backgroundColor: hasJoinedMeeting
                              ? "success.dark"
                              : "#007f80",
                            color: "#fff",
                          },
                          width: { xs: "110px", sm: "160px" },
                          fontSize: 13,
                          padding: "7px 4px",
                        }}
                        onClick={handleJoinMeeting}
                      >
                        {hasJoinedMeeting ? "Joined" : "Join Now"}
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                      >
                        Counselling Schedule
                      </Typography>
                      <Button
                        disabled={!getCounsellingData?.meetingLink}
                        variant="contained"
                        sx={{
                          borderRadius: 2,
                          backgroundColor: "#00999E",
                          "&:hover": {
                            backgroundColor: "#007f80",
                            color: "#fff",
                          },
                          width: { xs: "110px", sm: "160px" },
                          fontSize: 13,
                          padding: "7px 4px",
                        }}
                        onClick={handleDialogOpen}
                      >
                        Re-schedule
                      </Button>
                    </Box>
                  </Box>
                )
              )}

              {title === "Counselling" && (
                <>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 600, my: 4, color: "#00999E" }}
                    >
                      Student Counselling Log
                    </Typography>
                  </Box>
                  <Paper elevation={2} sx={{ mt: 2, p: 2 }}>
                    <Box sx={{ mt: 2 }}>
                      <BasicTable />
                    </Box>
                  </Paper>
                </>
              )}
            </>
          </Box>
        </Box>

        <Dialog open={isDialogOpen} onClose={handleDialogClose}>
          <DialogTitle>Re-schedule Meeting</DialogTitle>
          <DialogContent>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Select Date & Time"
                value={selectedDateTime}
                format="DD-MMM-YYYY hh:mm A"
                minDate={dayjs()}
                views={["year", "month", "day", "hours", "minutes"]}
                onChange={handleDateTimeChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{ my: 2 }}
                    error={!!errors.selectedDateTime}
                    helperText={errors.selectedDateTime}
                  />
                )}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button onClick={handleDialogClose} color="error">
              Cancel
            </Button>
            <Button
              onClick={handleRescheduleSubmit}
              color="primary"
              variant="contained"
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </LocalizationProvider>
  );
}
